import { create } from "zustand";
import {
  WorkspaceDocumentMetadata,
  MetadataEnum,
} from "@/models/metadata/metadata";

interface UploadMetadataState {
  // Store metadata for each document by filename
  documentMetadata: Record<string, WorkspaceDocumentMetadata[]>;

  // Current document index for navigation
  currentDocumentIndex: number;

  // Total number of documents
  totalDocuments: number;

  // Upload files
  uploadFiles: FileList | null;

  // Current document name (for edit mode)
  currentDocumentName: string | null;

  // Workspace file names mapping
  workspaceFileNames: Record<string, string>;

  // Folder ID for upload
  folderId?: string;

  // Actions
  setDocumentMetadata: (
    filename: string,
    metadata: WorkspaceDocumentMetadata[]
  ) => void;
  getDocumentMetadata: (filename: string) => WorkspaceDocumentMetadata[];
  setCurrentDocumentIndex: (index: number) => void;
  setTotalDocuments: (total: number) => void;
  setUploadFiles: (files: FileList | null) => void;
  setWorkspaceFileNames: (names: Record<string, string>) => void;
  setFolderId: (id?: string) => void;
  setCurrentDocumentName: (name: string | null) => void;

  // Navigation helpers
  goToNextDocument: () => void;
  goToPreviousDocument: () => void;
  canGoNext: () => boolean;
  canGoPrevious: () => void;

  // Get current document info
  getCurrentDocument: () => File | null;
  getCurrentDocumentName: () => string | null;

  // Clear all state
  clearAll: () => void;

  // Get all metadata for upload
  getAllMetadata: () => WorkspaceDocumentMetadata[][];

  // Initialize metadata for a document from different sources
  initializeDocumentMetadata: (
    documentName: string,
    source: "edit" | "upload",
    data: {
      editDocument?: { documentMetadataList: WorkspaceDocumentMetadata[] };
      extractionResult?: {
        hasCustomProperties: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        customProperties: any[];
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tenantFields?: any[];
    }
  ) => void;

  // Get current document's field values
  getFieldValue: (
    fieldName: string
  ) => number | string | boolean | Date[] | string[] | undefined;

  // Set current document's field value
  setFieldValue: (
    fieldName: string,
    value: number | string | boolean | Date[] | string[] | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fieldDefinition: any
  ) => void;

  // Delete field from current document
  deleteField: (fieldName: string) => void;
}

export const useUploadMetadataStore = create<UploadMetadataState>(
  (set, get) => ({
    documentMetadata: {},
    currentDocumentIndex: 0,
    totalDocuments: 0,
    uploadFiles: null,
    currentDocumentName: null,
    workspaceFileNames: {},
    folderId: undefined,

    setDocumentMetadata: (filename, metadata) =>
      set((state) => ({
        documentMetadata: {
          ...state.documentMetadata,
          [filename]: metadata,
        },
      })),

    getDocumentMetadata: (filename) => {
      const state = get();
      return state.documentMetadata[filename] || [];
    },

    setCurrentDocumentIndex: (index) => set({ currentDocumentIndex: index }),

    setTotalDocuments: (total) => set({ totalDocuments: total }),

    setUploadFiles: (files) => set({ uploadFiles: files }),

    setWorkspaceFileNames: (names) => set({ workspaceFileNames: names }),

    setFolderId: (id) => set({ folderId: id }),

    setCurrentDocumentName: (name) => set({ currentDocumentName: name }),

    goToNextDocument: () =>
      set((state) => {
        const nextIndex = Math.min(
          state.currentDocumentIndex + 1,
          state.totalDocuments - 1
        );
        return { currentDocumentIndex: nextIndex };
      }),

    goToPreviousDocument: () =>
      set((state) => {
        const prevIndex = Math.max(state.currentDocumentIndex - 1, 0);
        return { currentDocumentIndex: prevIndex };
      }),

    canGoNext: () => {
      const state = get();
      return state.currentDocumentIndex < state.totalDocuments - 1;
    },

    canGoPrevious: () => {
      const state = get();
      return state.currentDocumentIndex > 0;
    },

    getCurrentDocument: () => {
      const state = get();
      if (!state.uploadFiles) return null;
      return state.uploadFiles[state.currentDocumentIndex] || null;
    },

    getCurrentDocumentName: () => {
      const state = get();
      if (state.uploadFiles) {
        // For upload mode, get from upload files
        const currentFile = state.getCurrentDocument();
        return currentFile?.name || null;
      } else {
        // For edit mode, we need to get the document name from the store state
        // This will be set when the component initializes
        return state.currentDocumentName || null;
      }
    },

    clearAll: () =>
      set({
        documentMetadata: {},
        currentDocumentIndex: 0,
        totalDocuments: 0,
        uploadFiles: null,
        currentDocumentName: null,
        workspaceFileNames: {},
        folderId: undefined,
      }),

    getAllMetadata: () => {
      const state = get();
      if (!state.uploadFiles) return [];

      return Array.from(state.uploadFiles).map(
        (file) => state.documentMetadata[file.name] || []
      );
    },

    initializeDocumentMetadata: (documentName, source, data) => {
      const state = get();

      // Check if we already have metadata for this document
      if (state.documentMetadata[documentName]?.length > 0) return;

      const metadata: WorkspaceDocumentMetadata[] = [];

      if (source === "edit" && data.editDocument?.documentMetadataList) {
        // For edit mode, use existing document metadata
        metadata.push(...data.editDocument.documentMetadataList);
      } else if (
        source === "upload" &&
        data.extractionResult?.hasCustomProperties &&
        data.tenantFields
      ) {
        // For upload mode, extract from DOCX custom properties
        const normalizePropertyName = (name: string): string =>
          name.toLowerCase().replace(/\s+/g, "");

        data.extractionResult.customProperties.forEach((p) => {
          const normalizedDocxName = normalizePropertyName(p.name);

          const match = data.tenantFields!.find((t) => {
            if (!t.externalPropertyName) return false;
            const normalizedExternalName = normalizePropertyName(
              t.externalPropertyName
            );
            return normalizedExternalName === normalizedDocxName;
          });

          if (match) {
            const convertedValue =
              p.type === "number"
                ? parseFloat(p.value) || 0
                : p.type === "boolean"
                  ? p.value.toLowerCase() === "true"
                  : p.type === "date"
                    ? [new Date(p.value)]
                    : p.value;

            const metadataType = {
              stringValue:
                match.metadataTypeEnum === MetadataEnum.StringValue
                  ? (convertedValue as string)
                  : undefined,
              numberValue:
                match.metadataTypeEnum === MetadataEnum.NumberValue
                  ? (convertedValue as number)
                  : undefined,
              booleanValue:
                match.metadataTypeEnum === MetadataEnum.BooleanValue
                  ? (convertedValue as boolean)
                  : undefined,
              dateTimeValues:
                match.metadataTypeEnum === MetadataEnum.DateTimeValues
                  ? (convertedValue as Date[])
                  : undefined,
              selectValues:
                match.metadataTypeEnum === MetadataEnum.SelectValues
                  ? (convertedValue as string[])
                  : undefined,
            };

            metadata.push({
              name: match.name,
              externalPropertyName: match.externalPropertyName,
              metadata: metadataType,
            });
          }
        });
      }

      if (metadata.length > 0) {
        set((state) => ({
          documentMetadata: {
            ...state.documentMetadata,
            [documentName]: metadata,
          },
        }));
      }
    },

    getFieldValue: (fieldName) => {
      const state = get();
      const currentDocumentName = state.getCurrentDocumentName();
      if (!currentDocumentName) return undefined;

      const metadata = state.documentMetadata[currentDocumentName] || [];
      const meta = metadata.find((v) => v.name === fieldName);
      if (!meta || !meta.metadata) return undefined;

      return (
        meta.metadata.stringValue ??
        meta.metadata.numberValue ??
        meta.metadata.booleanValue ??
        meta.metadata.dateTimeValues ??
        meta.metadata.selectValues
      );
    },

    setFieldValue: (fieldName, value, fieldDefinition) => {
      const state = get();
      const currentDocumentName = state.getCurrentDocumentName();
      if (!currentDocumentName) return;

      const currentMetadata = state.documentMetadata[currentDocumentName] || [];
      const existing = currentMetadata.find((v) => v.name === fieldName);

      let newMetadata: WorkspaceDocumentMetadata[];

      // Determine the target type based on field definition
      let targetType: MetadataEnum | null = null;

      if (fieldDefinition.metadataTypeEnum) {
        // TenantDocumentMetadata
        targetType = fieldDefinition.metadataTypeEnum;
      } else if (fieldDefinition.metadata) {
        // WorkspaceDocumentMetadata - determine type from existing metadata
        const metadata = fieldDefinition.metadata;
        if (
          metadata.stringValue !== null &&
          metadata.stringValue !== undefined
        ) {
          targetType = MetadataEnum.StringValue;
        } else if (
          metadata.numberValue !== null &&
          metadata.numberValue !== undefined
        ) {
          targetType = MetadataEnum.NumberValue;
        } else if (
          metadata.booleanValue !== null &&
          metadata.booleanValue !== undefined
        ) {
          targetType = MetadataEnum.BooleanValue;
        } else if (
          metadata.dateTimeValues !== null &&
          metadata.dateTimeValues !== undefined
        ) {
          targetType = MetadataEnum.DateTimeValues;
        } else if (
          metadata.selectValues !== null &&
          metadata.selectValues !== undefined
        ) {
          targetType = MetadataEnum.SelectValues;
        }
      }

      if (existing) {
        newMetadata = currentMetadata.map((v) =>
          v.name === fieldName
            ? {
                ...v,
                metadata: {
                  stringValue:
                    targetType === MetadataEnum.StringValue
                      ? (value as string)
                      : v.metadata?.stringValue,
                  numberValue:
                    targetType === MetadataEnum.NumberValue
                      ? (value as number)
                      : v.metadata?.numberValue,
                  booleanValue:
                    targetType === MetadataEnum.BooleanValue
                      ? (value as boolean)
                      : v.metadata?.booleanValue,
                  dateTimeValues:
                    targetType === MetadataEnum.DateTimeValues
                      ? (value as Date[])
                      : v.metadata?.dateTimeValues,
                  selectValues:
                    targetType === MetadataEnum.SelectValues
                      ? (value as string[])
                      : v.metadata?.selectValues,
                },
              }
            : v
        );
      } else {
        const metadataType = {
          stringValue:
            targetType === MetadataEnum.StringValue
              ? (value as string)
              : undefined,
          numberValue:
            targetType === MetadataEnum.NumberValue
              ? (value as number)
              : undefined,
          booleanValue:
            targetType === MetadataEnum.BooleanValue
              ? (value as boolean)
              : undefined,
          dateTimeValues:
            targetType === MetadataEnum.DateTimeValues
              ? (value as Date[])
              : undefined,
          selectValues:
            targetType === MetadataEnum.SelectValues
              ? (value as string[])
              : undefined,
        };

        newMetadata = [
          ...currentMetadata,
          {
            name: fieldName,
            externalPropertyName: fieldDefinition.externalPropertyName,
            metadata: metadataType,
          },
        ];
      }

      set((state) => ({
        documentMetadata: {
          ...state.documentMetadata,
          [currentDocumentName]: newMetadata,
        },
      }));
    },

    deleteField: (fieldName) => {
      const state = get();
      const currentDocumentName = state.getCurrentDocumentName();
      if (!currentDocumentName) return;

      const currentMetadata = state.documentMetadata[currentDocumentName] || [];
      const newMetadata = currentMetadata.filter((v) => v.name !== fieldName);

      set((state) => ({
        documentMetadata: {
          ...state.documentMetadata,
          [currentDocumentName]: newMetadata,
        },
      }));
    },
  })
);
